import { makeStyles } from "@material-ui/core/styles";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import { RefObject, useEffect, useRef, useState } from "react";
import {
    getAppState,
    useAppState,
} from "../../../../../../../../common/appState";
import { sleepAsync } from "../../../../../../../../common/functions";
import { InitialView } from "../../../MyPageTop/types";
import { OpenableCard } from "../OpenableCard";
import { ClaimAllButton } from "./ClaimAllButton";
import {
    QuestRecord,
    myPageQuestCardScrollableContainerHeight,
} from "./QuestRecord";
import { ClearedQuest, Quest } from "./quests/types";

const cardId = "MyPageQuestsCard";

export function QuestsCard({
    initialView,
    scrollableContainerRef,
}: {
    initialView?: InitialView;
    scrollableContainerRef: RefObject<HTMLDivElement>;
}) {
    const [open, setOpen] = useState(false);
    const [{ clearedQuests, ongoingQuests, exclamationMark }] =
        useAppState("questState");

    const c = useStyles();

    return (
        <OpenableCard
            title={
                <span>
                    Quests:{" "}
                    <span className={c.titleNumber}>
                        {clearedQuests.length + ongoingQuests.length}
                    </span>
                </span>
            }
            icon={<AssignmentTurnedInIcon />}
            saveKey={cardId}
            id={cardId}
            open={open}
            setOpen={setOpen}
            alwaysShowIcon
            alwaysShowTitle
            initiallyOpenedId={initialView}
            exclamationMark={exclamationMark}
            scrollableContainerRef={scrollableContainerRef}
        >
            <QuestList
                ongoingQuests={ongoingQuests}
                clearedQuests={clearedQuests}
                open={open}
                isInitialView={initialView === cardId}
            />
        </OpenableCard>
    );
}
const useStyles = makeStyles(theme => ({
    titleNumber: { color: theme.palette.success.dark },
}));

function QuestList({
    ongoingQuests,
    clearedQuests,
    open,
    isInitialView,
}: {
    ongoingQuests: Quest[];
    clearedQuests: ClearedQuest[];
    open: boolean;
    isInitialView: boolean;
}) {
    const c = useListStyles();

    const { questKeyToScrollTo } = useQuestKeyToScroll(
        ongoingQuests,
        clearedQuests,
        open,
        isInitialView
    );
    const scrollableContainerRef = useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = useState(0);
    useEffect(() => {
        const el = scrollableContainerRef.current;
        if (!el) {
            return;
        }
        const onScroll = () => {
            setScrollTop(el.scrollTop);
        };
        setScrollTop(el.scrollTop);
        el.addEventListener("scroll", onScroll);
        return () => {
            el.removeEventListener("scroll", onScroll);
        };
    }, []);

    const [claimingAll, setClaimingAll] = useState(false);

    return (
        <>
            <ClaimAllButton
                submitting={claimingAll}
                setSubmitting={setClaimingAll}
                clearedQuests={clearedQuests}
            />

            <div className={c.container} ref={scrollableContainerRef}>
                {ongoingQuests.map((quest, i) => (
                    <QuestRecord
                        key={quest.questKey}
                        quest={quest}
                        isOngoing
                        questKeyToScrollTo={questKeyToScrollTo}
                        scrollableElement={scrollableContainerRef.current}
                        claimingAll={claimingAll}
                        scrollTop={scrollTop}
                        index={i}
                    />
                ))}
                {clearedQuests.map((quest, i) => (
                    <QuestRecord
                        key={quest.questKey}
                        quest={quest}
                        claimed={quest.claimed}
                        questKeyToScrollTo={questKeyToScrollTo}
                        scrollableElement={scrollableContainerRef.current}
                        claimingAll={claimingAll}
                        scrollTop={scrollTop}
                        index={ongoingQuests.length + i}
                    />
                ))}
                {ongoingQuests.length + clearedQuests.length === 0 && (
                    <div className={c.noData}>No Data</div>
                )}
            </div>
        </>
    );
}
const useListStyles = makeStyles(theme => ({
    container: {
        backgroundColor: theme.palette.grey[100],
        height: myPageQuestCardScrollableContainerHeight,
        maxHeight: myPageQuestCardScrollableContainerHeight,
        flex: 1,
        fontSize: "large",
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative",
    },
    noData: {
        width: "100%",
        height: myPageQuestCardScrollableContainerHeight - 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: theme.palette.grey[600],
    },
}));

function useQuestKeyToScroll(
    _ongoingQuests: Quest[],
    _clearedQuests: ClearedQuest[],
    open: boolean,
    isInitialView: boolean
) {
    const [questKeyToScrollTo, setQuestKeyToScrollTo] = useState("");

    const ongoingKeys = _ongoingQuests.map(q => q.questKey).join("_");
    const claimedKeys = _clearedQuests
        .filter(q => q.claimed)
        .map(q => q.questKey)
        .join("_");

    useEffect(() => {
        if (!open) {
            return;
        }
        (async () => {
            setQuestKeyToScrollTo(""); // Reset to fire scroll
            await sleepAsync(300); // Wait for resetting

            const { clearedQuests, ongoingQuests } = getAppState().questState;

            const unclaimedQuestKey = clearedQuests.find(
                q => !q.claimed
            )?.questKey;
            if (unclaimedQuestKey) {
                setQuestKeyToScrollTo(unclaimedQuestKey);
                return;
            }
            if (ongoingQuests.length > 0) {
                setQuestKeyToScrollTo(ongoingQuests[0].questKey);
            }
        })();
    }, [open, isInitialView, ongoingKeys, claimedKeys]);

    return { questKeyToScrollTo };
}
